import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Budget, TransactionCategory } from '../../types/financial';
import { formatCurrency } from '../../utils/pdfGenerator';
import './Financial.css';

interface BudgetManagerProps {
  budgets: Budget[];
  categories: TransactionCategory[];
  loading?: boolean;
  onCreate: (budget: Omit<Budget, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onUpdate: (budgetId: string, updates: Partial<Budget>) => Promise<void>;
  onDelete: (budgetId: string) => Promise<void>;
  onGetCategorySpent?: (categoryId: string, month?: number, year?: number) => Promise<number>;
}

export const BudgetManager: React.FC<BudgetManagerProps> = ({
  budgets,
  categories,
  loading,
  onCreate,
  onUpdate,
  onDelete,
  onGetCategorySpent,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [period, setPeriod] = useState<'mensal' | 'anual'>('mensal');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [alertThreshold, setAlertThreshold] = useState('80');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [categorySpent, setCategorySpent] = useState<Record<string, number>>({});

  useEffect(() => {
    if (onGetCategorySpent) {
      const loadSpent = async () => {
        const spent: Record<string, number> = {};
        for (const budget of budgets) {
          if (budget.period === 'mensal' && budget.month) {
            const spentAmount = await onGetCategorySpent(budget.categoryId, budget.month, budget.year);
            spent[budget.id] = spentAmount;
          } else if (budget.period === 'anual') {
            const spentAmount = await onGetCategorySpent(budget.categoryId, undefined, budget.year);
            spent[budget.id] = spentAmount;
          }
        }
        setCategorySpent(spent);
      };
      loadSpent();
    }
  }, [budgets, onGetCategorySpent]);

  const handleOpenModal = (budget?: Budget) => {
    if (budget) {
      setEditingBudget(budget);
      setCategoryId(budget.categoryId);
      setAmount(budget.amount.toFixed(2).replace('.', ','));
      setPeriod(budget.period);
      setMonth(budget.month || new Date().getMonth() + 1);
      setYear(budget.year);
      setAlertThreshold(budget.alertThreshold?.toString() || '80');
    } else {
      setEditingBudget(null);
      setCategoryId('');
      setAmount('');
      setPeriod('mensal');
      setMonth(new Date().getMonth() + 1);
      setYear(new Date().getFullYear());
      setAlertThreshold('80');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingBudget(null);
    setCategoryId('');
    setAmount('');
    setPeriod('mensal');
    setMonth(new Date().getMonth() + 1);
    setYear(new Date().getFullYear());
    setAlertThreshold('80');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryId) {
      alert('Selecione uma categoria');
      return;
    }

    const amountNum = parseFloat(amount.replace(',', '.')) || 0;
    if (amountNum <= 0) {
      alert('O valor deve ser maior que zero');
      return;
    }

    setSaving(true);
    try {
      const category = categories.find(c => c.id === categoryId);
      if (!category) {
        alert('Categoria não encontrada');
        return;
      }

      const budgetData: Omit<Budget, 'id' | 'createdAt' | 'updatedAt'> = {
        categoryId,
        categoryName: category.name,
        amount: amountNum,
        period,
        month: period === 'mensal' ? month : undefined,
        year,
        alertThreshold: parseInt(alertThreshold) || undefined,
      };

      if (editingBudget) {
        await onUpdate(editingBudget.id, budgetData);
      } else {
        await onCreate(budgetData);
      }
      handleCloseModal();
    } catch (error: any) {
      alert(error.message || 'Erro ao salvar meta');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (budget: Budget) => {
    if (!window.confirm(`Tem certeza que deseja excluir esta meta?`)) {
      return;
    }

    setDeletingId(budget.id);
    try {
      await onDelete(budget.id);
    } catch (error: any) {
      alert(error.message || 'Erro ao excluir meta');
    } finally {
      setDeletingId(null);
    }
  };

  const getBudgetStatus = (budget: Budget) => {
    const spent = categorySpent[budget.id] || 0;
    const percentage = (spent / budget.amount) * 100;
    const threshold = budget.alertThreshold || 80;

    if (spent >= budget.amount) {
      return { status: 'excedido', percentage, color: 'var(--theme-danger)' };
    } else if (percentage >= threshold) {
      return { status: 'alerta', percentage, color: '#f59e0b' };
    } else {
      return { status: 'ok', percentage, color: '#10b981' };
    }
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  const despesaCategories = categories.filter(c => c.type === 'despesa');

  return (
    <>
      <Card
        title="Metas Orçamentárias"
        actions={
          <Button onClick={() => handleOpenModal()}>
            Nova Meta
          </Button>
        }
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Carregando metas...</p>
          </div>
        ) : budgets.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Nenhuma meta orçamentária definida.</p>
          </div>
        ) : (
          <div className="ns-table-wrapper">
            <table className="ns-table ns-financial-consultations-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Período</th>
                  <th>Meta</th>
                  <th>Gasto</th>
                  <th>Progresso</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {budgets.map((budget) => {
                  const spent = categorySpent[budget.id] || 0;
                  const status = getBudgetStatus(budget);
                  return (
                    <tr key={budget.id}>
                      <td>{budget.categoryName}</td>
                      <td>
                        {budget.period === 'mensal'
                          ? `${monthNames[(budget.month || 1) - 1]}/${budget.year}`
                          : `${budget.year}`}
                      </td>
                      <td>{formatCurrency(budget.amount)}</td>
                      <td>{formatCurrency(spent)}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              flex: 1,
                              height: '8px',
                              backgroundColor: '#e2e8f0',
                              borderRadius: '4px',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(status.percentage, 100)}%`,
                                height: '100%',
                                backgroundColor: status.color,
                                transition: 'width 0.3s ease',
                              }}
                            />
                          </div>
                          <span style={{ fontSize: '12px', color: 'var(--page-text-muted)' }}>
                            {status.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: `${status.color}20`,
                            color: status.color,
                          }}
                        >
                          {status.status === 'excedido'
                            ? 'Excedido'
                            : status.status === 'alerta'
                            ? 'Alerta'
                            : 'OK'}
                        </span>
                      </td>
                      <td>
                        <div className="ns-financial-actions">
                          <Button
                            variant="ghost"
                            onClick={() => handleOpenModal(budget)}
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            onClick={() => handleDelete(budget)}
                            disabled={deletingId === budget.id}
                            style={{
                              fontSize: '12px',
                              padding: '4px 8px',
                              color: 'var(--theme-danger)',
                            }}
                          >
                            {deletingId === budget.id ? 'Excluindo...' : 'Excluir'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingBudget ? 'Editar Meta' : 'Nova Meta Orçamentária'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </>
        }
      >
        <form className="ns-form" onSubmit={handleSubmit}>
          <label className="ns-field">
            <span>Categoria</span>
            <select
              className="ns-input"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
            >
              <option value="">Selecione uma categoria</option>
              {despesaCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>

          <div className="ns-form-row">
            <label className="ns-field">
              <span>Valor (R$)</span>
              <input
                className="ns-input"
                type="text"
                value={amount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,.-]/g, '');
                  setAmount(value);
                }}
                placeholder="0,00"
                required
              />
            </label>

            <label className="ns-field">
              <span>Período</span>
              <select
                className="ns-input"
                value={period}
                onChange={(e) => setPeriod(e.target.value as 'mensal' | 'anual')}
                required
              >
                <option value="mensal">Mensal</option>
                <option value="anual">Anual</option>
              </select>
            </label>
          </div>

          {period === 'mensal' && (
            <div className="ns-form-row">
              <label className="ns-field">
                <span>Mês</span>
                <select
                  className="ns-input"
                  value={month}
                  onChange={(e) => setMonth(parseInt(e.target.value))}
                  required
                >
                  {monthNames.map((name, index) => (
                    <option key={index + 1} value={index + 1}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="ns-field">
                <span>Ano</span>
                <input
                  className="ns-input"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value))}
                  min={2020}
                  max={2100}
                  required
                />
              </label>
            </div>
          )}

          {period === 'anual' && (
            <label className="ns-field">
              <span>Ano</span>
              <input
                className="ns-input"
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                min={2020}
                max={2100}
                required
              />
            </label>
          )}

          <label className="ns-field">
            <span>Alerta em (% do orçamento)</span>
            <input
              className="ns-input"
              type="number"
              value={alertThreshold}
              onChange={(e) => setAlertThreshold(e.target.value)}
              min={0}
              max={100}
              placeholder="80"
            />
            <small style={{ color: 'var(--page-text-muted)', fontSize: '12px' }}>
              Receber alerta quando atingir esta porcentagem do orçamento
            </small>
          </label>
        </form>
      </Modal>
    </>
  );
};

